import React, { useEffect, useMemo, useRef } from 'react';
import useScrollRestoration from 'utils/scrollRestoration';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Router from 'next/router';
import PropTypes from 'prop-types';
import getAppTheme from '@/constants/theme';
import { ScrollRestoreProvider } from 'context/ScrollRestoreContext';

import { ThemeProvider as MUIThemeProvider, CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import store from '@/context/store';
import MainLayout from '@/layouts/MainLayout';
import AdminLayout from '@/layouts/AdminLayout';
import '../styles/globals.css';
import '../styles/tailwind.css';
import '../styles/editor.css';
import '@/i18n';
import i18n from '@/i18n';
import Cookies from 'js-cookie';
import { setUser } from 'context/actions/auth';

import ProgressBar from '@badrap/bar-of-progress';

// Brand yellow — progress bar өнгө
const progress = new ProgressBar({
    size: 3,
    color: '#F5C518',
    className: 'bar-of-progress',
    delay: 100,
});

Router.events.on('routeChangeStart', progress.start);
Router.events.on('routeChangeComplete', progress.finish);
Router.events.on('routeChangeError', progress.finish);

function AppInitializer() {
    const dispatch = useDispatch();
    const lang = useSelector((s) => s.system?.lang);

    useEffect(() => {
        // Cookie-аас хэл уншиж i18n шинэчлэх
        if (typeof window !== 'undefined') {
            const chosenLang = Cookies.get('chosen_lang') || 'mn';
            i18n.changeLanguage(chosenLang);
            dispatch({ type: 'CHANGE_LANG', data: chosenLang });
        }

        loadStoredUser();
    }, []);

    // Хэл өөрчлөгдөх бүрт <html lang="..."> шинэчлэх
    useEffect(() => {
        if (typeof window !== 'undefined' && lang) {
            document.documentElement.lang = lang;
        }
    }, [lang]);

    const loadStoredUser = () => {
        try {
            const userRaw = localStorage.getItem('user');
            if (userRaw) {
                const user = JSON.parse(userRaw);
                if (user) setUser(dispatch, user);
            }
        } catch (e) {
            console.error('AppInitializer error', e);
        }
    };

    return null;
}

function MongolRefineryApp({ Component, pageProps }) {
    const pendingRestoreRef = useRef(null);
    useScrollRestoration(pendingRestoreRef);

    const router = useRouter();

    // MUI theme (light mode default)
    const theme = useMemo(() => getAppTheme('light'), []);

    // Admin route шалгах
    const isAdminRoute = router.pathname.startsWith('/admin');
    const isAdminLogin = router.pathname === '/admin';

    const getLayout =
        Component.getLayout ||
        ((page) => {
            if (isAdminLogin) return page; // Нэвтрэх хуудас layout-гүй
            if (isAdminRoute) return <AdminLayout>{page}</AdminLayout>;
            return <MainLayout>{page}</MainLayout>;
        });

    return (
        <Provider store={store}>
            <Head>
                {/* ── Үндсэн ── */}
                <title>
                    Монгол Газрын Тос Боловсруулах Үйлдвэр ТӨХХК
                </title>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, maximum-scale=5"
                />
                <meta charSet="utf-8" />
                <meta
                    name="description"
                    content="Монгол Газрын Тос Боловсруулах Үйлдвэр — Дорноговь аймгийн Сайншанд сумд баригдаж буй стратегийн ач холбогдолтой үйлдвэр."
                />
                <meta
                    name="keywords"
                    content="Mongol Refinery, газрын тос, боловсруулах үйлдвэр, refinery, oil refinery Mongolia, EPC, Сайншанд"
                />
                <meta name="author" content="Mongol Refinery LLC" />
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
                <meta name="theme-color" content="#0F1419" media="(prefers-color-scheme: dark)" />
                <meta name="theme-color" content="#F8F8F6" media="(prefers-color-scheme: light)" />
                <meta name="format-detection" content="telephone=yes" />

                {/* ── Favicon ── */}
                <link rel="icon" href="/favicon.ico" sizes="any" />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="16x16"
                    href="/favicon-16x16.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="/favicon-32x32.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="/apple-touch-icon.png"
                />
                <link rel="manifest" href="/site.webmanifest" />

                {/* ── Open Graph ── */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:site_name"
                    content="Монгол Газрын Тос Боловсруулах Үйлдвэр"
                />
                <meta
                    property="og:title"
                    content="Монгол Газрын Тос Боловсруулах Үйлдвэр ТӨХХК"
                />
                <meta
                    property="og:description"
                    content="Монгол Улсын стратегийн ач холбогдолтой эрчим хүчний бүтээн байгуулалт."
                />
                <meta
                    property="og:url"
                    content={
                        process.env.NEXT_PUBLIC_SITE_URL ||
                        'https://mongolrefinery.mn'
                    }
                />
                <meta property="og:image" content={`${(process.env.NEXT_PUBLIC_SITE_URL || 'https://mongolrefinery.mn')}/og-image.jpg`} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:locale" content="mn_MN" />
                <meta property="og:locale:alternate" content="en_US" />

                {/* ── Twitter Card ── */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Монгол Газрын Тос Боловсруулах Үйлдвэр"
                />
                <meta
                    name="twitter:description"
                    content="Монгол Улсын стратегийн ач холбогдолтой эрчим хүчний бүтээн байгуулалт."
                />
                <meta name="twitter:image" content={`${(process.env.NEXT_PUBLIC_SITE_URL || 'https://mongolrefinery.mn')}/og-image.jpg`} />

                {/* ── Canonical ── */}
                <link
                    rel="canonical"
                    href={
                        process.env.NEXT_PUBLIC_SITE_URL ||
                        'https://mongolrefinery.mn'
                    }
                />

                {/* ── Hreflang (URD §5.5 SEO) ── */}
                <link
                    rel="alternate"
                    hrefLang="mn"
                    href={
                        process.env.NEXT_PUBLIC_SITE_URL ||
                        'https://mongolrefinery.mn'
                    }
                />
                <link
                    rel="alternate"
                    hrefLang="en"
                    href={
                        process.env.NEXT_PUBLIC_SITE_URL ||
                        'https://mongolrefinery.mn'
                    }
                />
                <link
                    rel="alternate"
                    hrefLang="x-default"
                    href={
                        process.env.NEXT_PUBLIC_SITE_URL ||
                        'https://mongolrefinery.mn'
                    }
                />

                {/* ── Schema.org Organization ── */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: 'Монгол Газрын Тос Боловсруулах Үйлдвэр ТӨХХК',
                            alternateName: 'Mongol Refinery',
                            url:
                                process.env.NEXT_PUBLIC_SITE_URL ||
                                'https://mongolrefinery.mn',
                            logo: `${(process.env.NEXT_PUBLIC_SITE_URL || 'https://mongolrefinery.mn')}/logo.png`,
                            sameAs: [
                                'https://www.facebook.com/Mongolrefinery',
                                'https://twitter.com/mongolrefinery',
                                'https://www.youtube.com/@-mongolrefinery9016',
                            ],
                            address: {
                                '@type': 'PostalAddress',
                                streetAddress:
                                    'Богд тауэр, 900 тоот, Жигжиджавын гудамж 3',
                                addressLocality: 'Улаанбаатар',
                                addressCountry: 'MN',
                            },
                            telephone: '+976-7010-0389',
                            email: 'info@mongolrefinery.mn',
                        }),
                    }}
                />
            </Head>
            <ScrollRestoreProvider onReadyRef={pendingRestoreRef}>
                <MUIThemeProvider theme={theme}>
                    <CssBaseline />
                    <AppInitializer />

                    {/* ── Skip to content (WCAG 2.4.1) ── */}
                    <a
                        href="#main-content"
                        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:bg-brand-yellow focus:text-brand-dark focus:px-4 focus:py-2 focus:font-bold"
                    >
                        Үндсэн контент руу шилжих
                    </a>

                    {getLayout(<Component {...pageProps} />)}
                </MUIThemeProvider>
            </ScrollRestoreProvider>
        </Provider>
    );
}

MongolRefineryApp.propTypes = {
    Component: PropTypes.elementType.isRequired,
    pageProps: PropTypes.object.isRequired,
};

export default MongolRefineryApp;